/**
 * BookSeniorModal — Modal riusabile per prenotare Zoom con un Senior HCF Property.
 *
 * ★ 2026-08-14 (Fase 51) — Modal unificato usato ovunque nel sito pubblico:
 *   • Card unità disponibili (LombokUnitsTable / PropertyUnitsTable) → tasto "Info"
 *   • Path-section resort (ResortDetailPage) → tasti "Parla con Senior" / "Prenota call"
 *   • Documenti riservati (ProjectDocuments)
 *
 * Il modal presenta:
 *   1. Senior REGIONALE del paese del progetto (es. Mattia → Albania, Raffaele → Indonesia)
 *   2. CEO (Richard Demichele) sempre come opzione universale
 *   3. Al click → apre Calendly del senior scelto in nuova tab
 *
 * L'unit code (se presente) viene passato a Calendly come UTM per tracciare il contesto.
 */
import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Video, Award, Users, ArrowRight, Sparkles, ShieldCheck, ExternalLink,
} from 'lucide-react';
import { getSeniorsForSlug } from '../lib/countrySeniors';

/**
 * @param {object}   props
 * @param {boolean}  props.open
 * @param {function} props.onOpenChange
 * @param {string}   props.slug          project slug (es. 'lombok')
 * @param {object?}  props.context       optional: { unitCode?, subject?, sourceLabel? }
 */
export function BookSeniorModal({ open, onOpenChange, slug, context = {} }) {
  const { t } = useTranslation();
  const { country, flag, seniors } = getSeniorsForSlug(slug);
  const unitCode    = context.unitCode || null;
  const subject     = context.subject  || (unitCode ? `Info unità ${unitCode}` : 'Info progetto');
  const sourceLabel = context.sourceLabel || 'website';

  const openCalendly = (senior) => {
    const url = new URL(senior.calendly);
    // Query params leggibili in Calendly form
    if (unitCode) url.searchParams.set('a1', `Unità: ${unitCode}`);
    url.searchParams.set('utm_source', 'greenparadisehcf');
    url.searchParams.set('utm_medium', sourceLabel);
    url.searchParams.set('utm_campaign', slug || 'general');
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
    // Chiudo il modal dopo 300ms per dare tempo al browser di aprire la tab
    setTimeout(() => onOpenChange(false), 300);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="book-senior-modal"
        aria-describedby="book-senior-modal-desc"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[hsl(var(--primary))]">
            {t('resortDetail.bookSenior.dialogTitle', 'Prenota una call')}
          </DialogTitle>
          <DialogDescription id="book-senior-modal-desc">
            {unitCode ? (
              <Trans
                i18nKey="resortDetail.bookSenior.contextUnit"
                values={{ code: unitCode }}
                components={{ b: <b className="text-foreground" /> }}
              />
            ) : (
              <Trans
                i18nKey="resortDetail.bookSenior.contextProject"
                values={{ country, flag }}
                components={{ b: <b className="text-foreground" /> }}
              />
            )}
            {t('resortDetail.bookSenior.dialogDescription', 'Scegli con chi vuoi parlare — il senior HCF Property ti aggiornerà sullo stato del progetto, sui documenti riservati e sulle opzioni di acquisto.')}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-2.5">
          {seniors.map((s, idx) => (
            <button
              type="button"
              key={s.id}
              onClick={() => openCalendly(s)}
              className={`
                w-full text-left p-4 rounded-xl border-2 transition-all
                hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/5
                ${s.role === 'ceo'
                  ? 'border-[var(--gp-gold)]/40 bg-[var(--gp-gold)]/5'
                  : 'border-border bg-[var(--surface-2)]'
                }
              `}
              data-testid={`book-senior-option-${s.id}`}
            >
              <div className="flex items-start gap-3">
                {/* Avatar / iniziali */}
                <div
                  className={`
                    w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0
                    font-serif text-lg font-medium
                    ${s.role === 'ceo'
                      ? 'bg-[var(--gp-gold)] text-[hsl(var(--primary))]'
                      : 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
                    }
                  `}
                >
                  {s.avatar}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium text-foreground">{s.name}</span>
                    {s.role === 'ceo' && (
                      <Badge className="text-[10px] py-0 h-4 bg-[var(--gp-gold)]/20 text-[var(--gp-gold)] border-[var(--gp-gold)]/40">
                        <Award className="w-2.5 h-2.5 mr-0.5" /> {t('resortDetail.bookSenior.ceoBadge', 'CEO')}
                      </Badge>
                    )}
                    {idx === 0 && s.role !== 'ceo' && (
                      <Badge className="text-[10px] py-0 h-4 bg-emerald-500/15 text-emerald-700 border-emerald-500/30">
                        {t('resortDetail.bookSenior.localSeniorBadge', '{{flag}} Senior locale', { flag })}
                      </Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {s.title}
                  </div>
                  {s.scope && (
                    <div className="text-[10px] text-[var(--gp-gold)] mt-1 font-medium">
                      {s.scope}
                    </div>
                  )}
                </div>

                <div className="flex-shrink-0 self-center">
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[10px] text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Video className="w-3 h-3" /> {t('resortDetail.bookSenior.footerZoom', 'Zoom call one-to-one')}
            </span>
            <span className="inline-flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> {t('resortDetail.bookSenior.footerNoCommitment', 'Nessun impegno')}
            </span>
            <span className="inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> {t('resortDetail.bookSenior.footerOfficialDocs', 'Documentazione ufficiale')}
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default BookSeniorModal;
